/**
 * The storefront's job is to *show* the payment platform's behavior, so domain error codes are
 * mapped to human explanations instead of a bare failure banner — and outcomes that are the
 * platform working as designed (a store-billed tenant refusing web checkout — ADR-021) are
 * labeled as expected, not as errors. The raw code stays visible next to every explanation.
 */
import type { ErrorResponse } from '../api/types';

export interface ErrorExplanation {
  /** The backend's stable domain error code, shown verbatim alongside the explanation. */
  readonly code: string;
  /** True when the outcome is the platform behaving as designed, not a failure. */
  readonly expected: boolean;
  readonly explanation: string;
}

const EXPLANATIONS: Record<string, Omit<ErrorExplanation, 'code'>> = {
  'Payments.CheckoutNotSupported': {
    expected: true,
    explanation:
      'This tenant is store-billed (Google Play / Apple IAP — ADR-021): purchases happen ' +
      'inside the app store, so the platform refuses every web checkout with 409 by design. ' +
      'Seeing this answer is the correct behavior, not a failure.',
  },
  'Payments.UnknownProvider': {
    expected: false,
    explanation:
      "The tenant's selected payment provider is not composed into this environment's " +
      'payment host, so checkout cannot start here.',
  },
  'Payments.Disabled': {
    expected: false,
    explanation:
      "The tenant's provider selection exists but is switched off (enabled: false). " +
      'Re-enable it in the Tilgong Manager.',
  },
  'ProductAccess.OfferingNotFound': {
    expected: false,
    explanation:
      "The payment host does not know this accessId in the tenant's catalog — the offering " +
      'was removed or never seeded.',
  },
  'TenantPaymentConfig.NotConfigured': {
    expected: false,
    explanation:
      'The tenant has no payment provider selected yet. Pick one for it in the Tilgong Manager.',
  },
  'TestMerchant.PaymentHostUnreachable': {
    expected: false,
    explanation:
      'The payment host relay could not be reached — the storefront backend is up, but its ' +
      'call to the payment host failed.',
  },
  'TestMerchant.PaymentHostNotConfigured': {
    expected: false,
    explanation:
      'This environment has no payment host configured (a bare local run), so the relay has ' +
      'nowhere to send the request.',
  },
};

/** Maps a domain error to its explanation; unknown codes fall back to the backend's description. */
export function explainError(error: ErrorResponse): ErrorExplanation {
  const known = EXPLANATIONS[error.code];
  return known === undefined
    ? { code: error.code, expected: false, explanation: error.description }
    : { code: error.code, ...known };
}
